import { stretchFromSubfamily, stretchLabel } from '@monksflow/timplate-engine';

/**
 * What a font file says about itself, read out of its own OpenType `name` table.
 *
 * Read here rather than on the server because the bytes are already in the browser's hands at
 * upload: asking the server for them would cost a download per font to recover six strings the
 * uploader is holding. Nothing downstream treats these as verified -- they are what the file
 * claims, the same standing a filename has.
 *
 * Every field is optional and a file that declares nothing yields an empty object. A blank field
 * is a normal outcome, not a failure: plenty of fonts ship with no designer and no licence url,
 * and inventing a plausible value would be worse than leaving the panel honest about not knowing.
 */
export interface FontMetadata {
  format?: FontFormat;
  sizeBytes: number;
  familyName?: string;
  weight?: string;
  style?: 'normal' | 'italic';
  /**
   * usWidthClass 1-9, present only when the face declares a width other than normal.
   *
   * A width has no column of its own anywhere downstream, so `fontFamilyOf` folds it into the
   * family a row is filed under: without that, a Condensed Bold and a Bold are the same row twice.
   */
  stretch?: number;
  foundry?: string;
  designer?: string;
  copyright?: string;
  licenseDescription?: string;
  licenseUrl?: string;
  version?: string;
}

export type FontFormat = 'woff2' | 'woff' | 'ttf' | 'otf';

/** Name table ids worth reading. The rest describe the file for a type designer, not for a library. */
const NAME_ID = {
  copyright: 0,
  family: 1,
  subfamily: 2,
  version: 5,
  foundry: 8,
  designer: 9,
  licenseDescription: 13,
  licenseUrl: 14,
  /** The family as a typographer names it, e.g. "Inter" where id 1 says "Inter SemiBold". */
  typographicFamily: 16,
  /** The rest of the style, e.g. "Condensed Bold Italic" where id 2 says only "Bold Oblique". */
  typographicSubfamily: 17,
} as const;

const SFNT_HEADER_BYTES = 12;
const SFNT_ENTRY_BYTES = 16;
const WOFF_HEADER_BYTES = 44;
const WOFF_ENTRY_BYTES = 20;

const tagAt = (view: DataView, offset: number): string =>
  String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3),
  );

/**
 * Which container this is, from the four bytes every font opens with.
 *
 * Read from the file rather than from its extension or its MIME type, both of which are whatever
 * the person's operating system decided to call it. Undefined for anything unrecognised, which is
 * how a file that is not a font at all leaves here without a format rather than with a wrong one.
 */
const formatOf = (view: DataView): FontFormat | undefined => {
  const tag = tagAt(view, 0);
  if (tag === 'wOFF') return 'woff';
  if (tag === 'wOF2') return 'woff2';
  if (tag === 'OTTO') return 'otf';
  // 0x00010000 is the original TrueType version; `true` is Apple's variant of the same thing.
  if (view.getUint32(0) === 0x0001_0000 || tag === 'true' || tag === 'ttcf') return 'ttf';
  return undefined;
};

/** One table's bytes, decompressed where the container stored it compressed. */
type TableReader = (tag: string) => Promise<DataView | undefined>;

const inflate = async (data: Uint8Array): Promise<Uint8Array> => {
  // zlib, which is what WOFF wraps each compressed table in. Native rather than a bundled inflate:
  // shipping a decompressor to every visitor to read a licence string on the rare upload is a poor
  // trade, and the API is in every browser this app supports.
  const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
};

/**
 * A way to reach a table by tag, for whichever container this is.
 *
 * WOFF2 is deliberately absent: its tables are Brotli-compressed AND its glyf/loca tables are
 * re-encoded, so reading one means a decoder the size of the metadata it would recover. A woff2
 * upload therefore records its format and its size and leaves the rest blank, which the library
 * already draws as "not recorded".
 */
const tableReaderFor = (
  buffer: ArrayBuffer,
  view: DataView,
  format: FontFormat,
): TableReader | undefined => {
  if (format === 'woff2') return undefined;

  if (format === 'woff') {
    const count = view.getUint16(12);
    return async (tag) => {
      for (let index = 0; index < count; index += 1) {
        const entry = WOFF_HEADER_BYTES + index * WOFF_ENTRY_BYTES;
        if (tagAt(view, entry) !== tag) continue;

        const offset = view.getUint32(entry + 4);
        const compressedLength = view.getUint32(entry + 8);
        const originalLength = view.getUint32(entry + 12);
        if (offset + compressedLength > buffer.byteLength) return undefined;

        const bytes = new Uint8Array(buffer, offset, compressedLength);
        // Equal lengths mean the table was stored as-is, which WOFF allows per table.
        const table = compressedLength === originalLength ? bytes : await inflate(bytes.slice());
        return new DataView(table.buffer, table.byteOffset, table.byteLength);
      }
      return undefined;
    };
  }

  const count = view.getUint16(4);
  return (tag) => {
    for (let index = 0; index < count; index += 1) {
      const entry = SFNT_HEADER_BYTES + index * SFNT_ENTRY_BYTES;
      if (tagAt(view, entry) !== tag) continue;

      const offset = view.getUint32(entry + 8);
      const length = view.getUint32(entry + 12);
      // A directory pointing past the end is what a truncated download looks like. Treated as a
      // table that is not there, so the format stays known rather than the whole read failing.
      if (offset + length > buffer.byteLength) return Promise.resolve(undefined);

      return Promise.resolve(new DataView(buffer, offset, length));
    }
    return Promise.resolve(undefined);
  };
};

/**
 * One name record's string, decoded by the platform that wrote it.
 *
 * Windows records are UTF-16BE and Macintosh ones are Mac Roman, whose lower half is ASCII. Latin-1
 * is close enough for the accented characters a foundry name uses and does not throw on the rest,
 * which matters more here than being exactly right about a currency symbol in a copyright line.
 */
const decodeName = (bytes: Uint8Array, platformId: number): string =>
  new TextDecoder(platformId === 3 ? 'utf-16be' : 'latin1').decode(bytes).trim();

const WINDOWS_PLATFORM = 3;
const US_ENGLISH = 0x0409;

/**
 * Every name the file declares, best record per id.
 *
 * A font commonly carries the same name several times over, once per platform and language. English
 * on Windows is preferred because it is the one every foundry writes; anything else is taken only
 * when nothing better is there, so a Japanese-only family name is still better than no name.
 */
const readNames = (table: DataView): Map<number, string> => {
  const count = table.getUint16(2);
  const stringsAt = table.getUint16(4);
  const names = new Map<number, string>();
  const preferred = new Set<number>();

  for (let index = 0; index < count; index += 1) {
    const record = 6 + index * 12;
    if (record + 12 > table.byteLength) break;

    const platformId = table.getUint16(record);
    const languageId = table.getUint16(record + 4);
    const nameId = table.getUint16(record + 6);
    const length = table.getUint16(record + 8);
    const offset = stringsAt + table.getUint16(record + 10);
    if (offset + length > table.byteLength) continue;

    const isPreferred = platformId === WINDOWS_PLATFORM && languageId === US_ENGLISH;
    if (preferred.has(nameId) && !isPreferred) continue;

    const bytes = new Uint8Array(table.buffer, table.byteOffset + offset, length);
    const value = decodeName(bytes, platformId);
    if (value.length === 0) continue;

    names.set(nameId, value);
    if (isPreferred) preferred.add(nameId);
  }

  return names;
};

/** fsSelection bits 0 and 9, the file's own answer to the question its subfamily also answers. */
const FS_SELECTION_SLANTED = 0x01 | 0x200;

/**
 * Oblique counts as italic: the panel is saying which of the two shelves this belongs on.
 *
 * Read from both the flag and the words, because either can be the only one that says so: a
 * subfamily of "Oblique" against an unset flag is the common case in an older foundry's files,
 * and a subfamily the foundry left as "Regular" against a set flag is the rarer opposite.
 */
const styleOf = (
  os2: DataView | undefined,
  subfamily: string | undefined,
): 'normal' | 'italic' | undefined => {
  const flagged =
    os2 !== undefined && os2.byteLength >= 64 && (os2.getUint16(62) & FS_SELECTION_SLANTED) !== 0;
  if (subfamily === undefined) return flagged ? 'italic' : undefined;
  return flagged || /italic|oblique/i.test(subfamily) ? 'italic' : 'normal';
};

/**
 * usWeightClass, at a fixed offset in every OS/2 version. Read from there rather than inferred
 * from "Bold" in the subfamily, which cannot tell a Semibold from a Black.
 */
const weightOf = (os2: DataView | undefined): number | undefined => {
  if (os2 === undefined || os2.byteLength < 6) return undefined;
  const declared = os2.getUint16(4);
  // A few older files write the 1-9 class scale into a field the spec has always scaled 100-1000.
  return declared >= 1 && declared <= 9 ? declared * 100 : declared;
};

const STRETCH_NORMAL = 5;

/**
 * usWidthClass, overridden by a width the subfamily names.
 *
 * The override is not a nicety: Linotype's Helvetica Neue Condensed cuts declare width 5, normal,
 * in OS/2 and name their width in the subfamily alone. The engine's own face reader resolves the
 * conflict the same way and from the same keyword table, so a file's width means one thing here,
 * in the measurer, and in the CSS that comes out the other end.
 *
 * Undefined at normal width, so a face that is not a width variant carries no width.
 */
const stretchOf = (
  os2: DataView | undefined,
  subfamily: string | undefined,
): number | undefined => {
  const declared = os2 !== undefined && os2.byteLength >= 8 ? os2.getUint16(6) : STRETCH_NORMAL;
  const stretch = stretchFromSubfamily(subfamily) ?? declared;
  return stretch >= 1 && stretch <= 9 && stretch !== STRETCH_NORMAL ? stretch : undefined;
};

/**
 * The family a face is FILED under, which is its own family qualified by its width.
 *
 * `familyName` alone is the family a typographer names - id 16 gives "DM Sans" where id 1 says
 * "DM Sans Medium", and a weight folded into the family would split one typeface across a row per
 * cut. A WIDTH is the opposite case, because no field downstream carries one: leave it in the
 * subfamily and Helvetica Neue's Condensed Bold and its Bold become the same family, weight and
 * style twice over, and which of the two draws is whichever the browser or the measurer reached
 * first.
 *
 * Callers that write a font row use this rather than `familyName`. Callers that ask which typeface
 * a file belongs to - matching a pack's own token against what its archive ships, say - want
 * `familyName`, which is why both are here rather than one folded into the other.
 */
export const fontFamilyOf = (metadata: FontMetadata): string | undefined =>
  qualifyFamily(metadata.familyName, metadata.stretch);

/** The same rule for a family that did not come from a file, e.g. one a pack's tokens name. */
export const qualifyFamily = (
  family: string | undefined,
  stretch: number | undefined,
): string | undefined => {
  if (family === undefined || stretch === undefined) return family;
  const label = stretchLabel(stretch);
  // Id 1 usually spells the width already ("Helvetica Neue Condensed"), and id 16 falls back to
  // id 1, so a second copy of the word is a real possibility rather than a defensive one.
  if (label === null || stretchFromSubfamily(family) !== null) return family;
  return `${family} ${label}`;
};

const entries = (metadata: Record<string, string | undefined>): Record<string, string> =>
  Object.fromEntries(
    Object.entries(metadata).filter(([, value]) => value !== undefined && value.length > 0),
  ) as Record<string, string>;

/**
 * Reads what a font file declares about itself, total rather than throwing.
 *
 * A file that cannot be parsed is not an upload that should fail: the bytes are still a font as far
 * as everything else is concerned, and refusing one because its `name` table is malformed would
 * block an upload over metadata nobody asked for. The size is always known, so that much always
 * comes back.
 */
export const readFontMetadata = async (file: File): Promise<FontMetadata> => {
  const base: FontMetadata = { sizeBytes: file.size };

  try {
    const buffer = await file.arrayBuffer();
    if (buffer.byteLength < WOFF_HEADER_BYTES) return base;

    const view = new DataView(buffer);
    const format = formatOf(view);
    if (format === undefined) return base;

    const withFormat: FontMetadata = { ...base, format };
    const readTable = tableReaderFor(buffer, view, format);
    if (readTable === undefined) return withFormat;

    // eslint-disable-next-line i18next/no-literal-string -- an OpenType table tag, not copy
    const nameTable = await readTable('name');
    if (nameTable === undefined) return withFormat;

    const names = readNames(nameTable);
    // Id 17 before id 2, because a style-linked face carries its width in 17 and nowhere else:
    // Helvetica Neue's Condensed Bold Oblique says "Condensed Bold Italic" there and only "Bold
    // Oblique" in id 2, so reading id 2 alone loses the one word that tells it from plain Bold.
    const subfamily = names.get(NAME_ID.typographicSubfamily) ?? names.get(NAME_ID.subfamily);

    // eslint-disable-next-line i18next/no-literal-string -- an OpenType table tag, not copy
    const os2 = await readTable('OS/2');
    const weight = weightOf(os2);
    const stretch = stretchOf(os2, subfamily);
    const style = styleOf(os2, subfamily);

    return {
      ...withFormat,
      ...entries({
        familyName: names.get(NAME_ID.typographicFamily) ?? names.get(NAME_ID.family),
        weight: weight === undefined ? undefined : String(weight),
        foundry: names.get(NAME_ID.foundry),
        designer: names.get(NAME_ID.designer),
        copyright: names.get(NAME_ID.copyright),
        licenseDescription: names.get(NAME_ID.licenseDescription),
        licenseUrl: names.get(NAME_ID.licenseUrl),
        version: names.get(NAME_ID.version),
      }),
      ...(style !== undefined ? { style } : {}),
      ...(stretch !== undefined ? { stretch } : {}),
    };
  } catch {
    return base;
  }
};
